import { AdminRole } from '../../config/admin-nav.config';

export type NotificationCategory =
  | 'registration'
  | 'queue'
  | 'pharmacy'
  | 'payment'
  | 'appointment'
  | 'system';

export type NotificationPriority = 'high' | 'normal' | 'low';
export type NotificationFilter = 'all' | 'action' | 'queue' | 'payments';

export type ClinicNotificationType =
  | 'patient_checked_in'
  | 'appointment_completed'
  | 'payment_received'
  | 'self_registration'
  | 'patient_created'
  | 'medicine_queue'
  | 'appointment_created'
  | 'appointment_cancelled'
  | 'appointment_paused'
  | 'cross_clinic_waiting'
  | 'dues_settled';

export interface AppNotification {
  id: string;
  type: ClinicNotificationType | string;
  category: NotificationCategory;
  priority: NotificationPriority;
  title: string;
  detail: string;
  path?: string;
  createdAt: string | null;
  read: boolean;
  dismissible: boolean;
  sound?: boolean;
  actorUserId?: number | null;
  clinicId?: number | null;
  doctorId?: number | null;
  meta?: Record<string, unknown>;
}

export interface NotificationsSnapshot {
  items: AppNotification[];
  unreadCount: number;
  loading: boolean;
  lastUpdatedAt: string | null;
  error: string | null;
}

export const EMPTY_NOTIFICATIONS_SNAPSHOT: NotificationsSnapshot = {
  items: [],
  unreadCount: 0,
  loading: false,
  lastUpdatedAt: null,
  error: null,
};

/** Doctor — consult-critical alerts + major visit outcomes with sound. */
const DOCTOR_POLICY: Record<string, { visible: boolean; sound: boolean }> = {
  patient_checked_in: { visible: true, sound: true },
  cross_clinic_waiting: { visible: true, sound: true },
  self_registration: { visible: true, sound: false },
  patient_created: { visible: false, sound: false },
  medicine_queue: { visible: true, sound: false },
  appointment_completed: { visible: true, sound: true },
  payment_received: { visible: true, sound: true },
  dues_settled: { visible: false, sound: false },
  appointment_created: { visible: true, sound: false },
  appointment_cancelled: { visible: true, sound: false },
  appointment_paused: { visible: true, sound: false },
};

/** Reception desk — front-desk workflow sounds. */
const RECEPTION_POLICY: Record<string, { visible: boolean; sound: boolean }> = {
  patient_checked_in: { visible: true, sound: false },
  cross_clinic_waiting: { visible: true, sound: false },
  self_registration: { visible: true, sound: true },
  patient_created: { visible: true, sound: true },
  medicine_queue: { visible: true, sound: true },
  appointment_completed: { visible: true, sound: true },
  payment_received: { visible: true, sound: true },
  dues_settled: { visible: true, sound: true },
  appointment_created: { visible: true, sound: false },
  appointment_cancelled: { visible: true, sound: false },
  appointment_paused: { visible: false, sound: false },
};

const PHARMACY_POLICY: Record<string, { visible: boolean; sound: boolean }> = {
  medicine_queue: { visible: true, sound: true },
  appointment_completed: { visible: false, sound: false },
  payment_received: { visible: false, sound: false },
  patient_checked_in: { visible: false, sound: false },
  self_registration: { visible: false, sound: false },
  patient_created: { visible: false, sound: false },
};

const ASSISTANT_POLICY: Record<string, { visible: boolean; sound: boolean }> = {
  self_registration: { visible: true, sound: true },
  patient_created: { visible: true, sound: false },
  patient_checked_in: { visible: true, sound: false },
};

const POLICY_BY_ROLE: Partial<Record<AdminRole, Record<string, { visible: boolean; sound: boolean }>>> = {
  doctor: DOCTOR_POLICY,
  reception: RECEPTION_POLICY,
  pharmacy: PHARMACY_POLICY,
  assistant_doctor: ASSISTANT_POLICY,
};

export function getNotificationPolicy(
  role: unknown,
  type: string,
): { visible: boolean; sound: boolean } {
  const table = POLICY_BY_ROLE[role as AdminRole];
  if (!table) {
    return { visible: false, sound: false };
  }
  return table[type] ?? { visible: false, sound: false };
}

export function categoryLabel(category: NotificationCategory): string {
  switch (category) {
    case 'registration':
      return 'Registration';
    case 'queue':
      return 'Queue';
    case 'pharmacy':
      return 'Pharmacy';
    case 'payment':
      return 'Payment';
    case 'appointment':
      return 'Appointment';
    default:
      return 'Clinic';
  }
}

export function categoryIcon(category: NotificationCategory): string {
  switch (category) {
    case 'registration':
      return 'pi-inbox';
    case 'queue':
      return 'pi-users';
    case 'pharmacy':
      return 'pi-shopping-bag';
    case 'payment':
      return 'pi-wallet';
    case 'appointment':
      return 'pi-calendar';
    default:
      return 'pi-bell';
  }
}

export function formatNotificationTime(value?: string | null): string {
  if (!value) {
    return 'Just now';
  }
  const then = new Date(value).getTime();
  if (!Number.isFinite(then)) {
    return 'Just now';
  }
  const diffMin = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (diffMin < 1) {
    return 'Just now';
  }
  if (diffMin < 60) {
    return `${diffMin}m ago`;
  }
  const hours = Math.round(diffMin / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  return `${Math.round(hours / 24)}d ago`;
}
